import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SignerRole } from '@pdfsign/shared/types';

export interface SavedContact {
  id: string;
  full_name: string;
  email: string;
  title?: string;
  department?: string;
  phone?: string;
  role: SignerRole;
  signature_type: 'draw' | 'image' | 'text' | 'digital';
  created_at?: string;
  updated_at?: string;
}

export const DEFAULT_CONTACTS: SavedContact[] = [
  {
    id: 'contact-hang',
    full_name: 'Huỳnh Thị Thu Hằng',
    email: 'hang.huynh@coquan.gov.vn',
    title: 'Trưởng khoa / Trưởng phòng',
    department: 'Khoa Dược / Thiết bị y tế',
    phone: '0901234567',
    role: 'signer',
    signature_type: 'draw',
    created_at: new Date().toISOString(),
  },
  {
    id: 'contact-anh',
    full_name: 'Nguyễn Bùi Ngọc Ánh',
    email: 'anh.nguyen@coquan.gov.vn',
    title: 'Chuyên viên rà soát danh mục',
    department: 'Tổ rà soát thiết bị',
    phone: '0912345678',
    role: 'signer',
    signature_type: 'draw',
    created_at: new Date().toISOString(),
  },
  {
    id: 'contact-tram',
    full_name: 'Trương Thị Thùy Trâm',
    email: 'tram.truong@coquan.gov.vn',
    title: 'Phó trưởng phòng',
    department: 'Phòng Kế hoạch tổng hợp',
    phone: '0923456789',
    role: 'signer',
    signature_type: 'draw',
    created_at: new Date().toISOString(),
  },
  {
    id: 'contact-hoang',
    full_name: 'Lê Văn Hoàng',
    email: 'hoang.le@coquan.gov.vn',
    title: 'Giám đốc / Lãnh đạo phê duyệt',
    department: 'Ban Giám đốc',
    phone: '0934567890',
    role: 'approver',
    signature_type: 'draw',
    created_at: new Date().toISOString(),
  },
];

interface ContactState {
  contacts: SavedContact[];
  addContact: (contact: Omit<SavedContact, 'id' | 'created_at' | 'updated_at'>) => SavedContact;
  updateContact: (id: string, contact: Partial<SavedContact>) => void;
  deleteContact: (id: string) => void;
  deleteMultipleContacts: (ids: string[]) => void;
  resetDefaultContacts: () => void;
  importContacts: (contacts: SavedContact[]) => void;
  mergeOrganizationMembers: (members: Array<{ id: string; user_id?: string; full_name: string; email: string }>) => void;
}

export const useContactStore = create<ContactState>()(
  persist(
    (set, get) => ({
      contacts: DEFAULT_CONTACTS,

      addContact: (contactData) => {
        const newContact: SavedContact = {
          ...contactData,
          id: `contact-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        set((state) => ({
          contacts: [newContact, ...state.contacts.filter(c => c.email.toLowerCase() !== newContact.email.toLowerCase())],
        }));
        return newContact;
      },

      updateContact: (id, updateData) => {
        set((state) => ({
          contacts: state.contacts.map((c) =>
            c.id === id ? { ...c, ...updateData, updated_at: new Date().toISOString() } : c
          ),
        }));
      },

      deleteContact: (id) => {
        set((state) => ({
          contacts: state.contacts.filter((c) => c.id !== id),
        }));
      },

      deleteMultipleContacts: (ids) => {
        const setIds = new Set(ids);
        set((state) => ({
          contacts: state.contacts.filter((c) => !setIds.has(c.id)),
        }));
      },

      resetDefaultContacts: () => {
        set({ contacts: DEFAULT_CONTACTS });
      },

      importContacts: (imported) => {
        set((state) => {
          const map = new Map(state.contacts.map(c => [c.email.toLowerCase(), c]));
          for (const item of imported) {
            map.set(item.email.toLowerCase(), {
              ...item,
              id: item.id || `contact-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
              updated_at: new Date().toISOString(),
            });
          }
          return { contacts: Array.from(map.values()) };
        });
      },

      mergeOrganizationMembers: (members) => {
        set((state) => {
          const map = new Map(state.contacts.map(c => [c.email.toLowerCase(), c]));
          for (const m of members) {
            if (!m.email) continue;
            if (!map.has(m.email.toLowerCase())) {
              map.set(m.email.toLowerCase(), {
                id: `org-${m.user_id || m.id}`,
                full_name: m.full_name,
                email: m.email,
                title: 'Thành viên tổ chức',
                role: 'signer',
                signature_type: 'draw',
                created_at: new Date().toISOString(),
              });
            }
          }
          return { contacts: Array.from(map.values()) };
        });
      },
    }),
    {
      name: 'pdfsign_saved_contacts',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
